import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { trackEvent } from '@/utils/analytics';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQ {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  faqs: FAQ[];
  heading?: string;
}

const FAQSchema = ({ faqs, heading = "Frequently Asked Questions" }: FAQSchemaProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-[900px] mx-auto">
          <h2 className="text-[2rem] md:text-[2.5rem] font-light leading-tight text-black mb-12">{heading}</h2>
          <div className="space-y-0">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-black/10">
                <button
                  onClick={() => {
                    if (openIndex !== i) {
                      trackEvent('faq_expand', { question: faq.question });
                    }
                    setOpenIndex(openIndex === i ? null : i);
                  }}
                  className="w-full text-left flex justify-between items-center py-5 text-lg font-medium hover:text-[#002178] transition-colors"
                >
                  {faq.question}
                  <span className="text-2xl ml-4 flex-shrink-0">{openIndex === i ? '\u2212' : '+'}</span>
                </button>
                <AnimatePresence>
                  {openIndex === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <p className="text-black/60 pb-5 leading-relaxed">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQSchema;
